import React, { useState, useEffect, useRef } from 'react';
import { MatterDetailed } from '../../../../types/matters';
import { EVENT_TYPES } from '../../../calendar/constants/eventTypes';
import type { CalendarEvent } from '../../../calendar/types/calendar.types';
import { 
  CalendarDaysIcon, 
  PlusIcon,
  FunnelIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FolderIcon
} from '@heroicons/react/24/outline';

// Priority levels
const PRIORITY_LEVELS = [
  { id: 'urgent', label: 'Urgent', color: 'red' },
  { id: 'high', label: 'High', color: 'orange' },
  { id: 'medium', label: 'Medium', color: 'yellow' },
  { id: 'low', label: 'Low', color: 'green' },
];

interface MatterCalendarSidebarProps {
  matter: MatterDetailed;
  matterEvents: CalendarEvent[];
  matterEventTypeFilters: string[];
  matterPriorityFilters: string[];
  onToggleEventType: (eventType: string) => void;
  onTogglePriority: (priority: string) => void;
  onClearFilters: () => void;
  onCreateEvent: () => void;
}

const MatterCalendarSidebar: React.FC<MatterCalendarSidebarProps> = ({
  matter,
  matterEvents,
  matterEventTypeFilters,
  matterPriorityFilters,
  onToggleEventType,
  onTogglePriority,
  onClearFilters,
  onCreateEvent
}) => {
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(() => {
    const saved = localStorage.getItem('matterCalendarFiltersExpanded');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Save filter expanded state to localStorage
  const toggleFiltersExpanded = () => {
    const newState = !isFiltersExpanded;
    setIsFiltersExpanded(newState);
    localStorage.setItem('matterCalendarFiltersExpanded', JSON.stringify(newState));
  };

  const getPriorityColor = (color: string) => {
    switch (color) {
      case 'red': return '#ef4444';
      case 'orange': return '#f59e0b';
      case 'yellow': return '#eab308';
      case 'green': return '#10b981';
      default: return '#6b7280';
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCreateDropdownOpen(false);
      }
    };

    if (isCreateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isCreateDropdownOpen]);

  return (
    <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 h-full flex flex-col shadow-sm">
      {/* Matter Header */}
      <div className="px-3 py-3 border-b border-gray-200 dark:border-gray-700 bg-lex-accent-soft">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <FolderIcon className="h-5 w-5 text-lex-accent shrink-0" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {matter.title}
            </span>
          </div>
        </div>
        
        <div className="text-xs text-lex-accent space-y-1">
          <div>
            <span className="font-medium">Client:</span> {matter.client?.name || 'No client'}
          </div>
          <div>
            <span className="font-medium">Area:</span> {matter.practice_area}
          </div>
          <div>
            <span className="font-medium">Status:</span> {matter.status ? matter.status.replace('_', ' ') : 'Pending'}
          </div>
        </div>
        
        {/* Unified Create Button with Dropdown */}
        <div className="relative mt-3" ref={dropdownRef}>
          <button
            onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-lex-accent hover:bg-lex-accent-hover text-lex-accent-contrast rounded-md text-sm font-medium shadow-sm transition-colors"
          >
            <div className="flex items-center gap-2">
              <PlusIcon className="h-4 w-4" />
              Create
            </div>
            <ChevronDownIcon className={`h-4 w-4 transition-transform ${isCreateDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Create Dropdown Menu */}
          {isCreateDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50">
              <div className="py-1">
                <button
                  onClick={() => {
                    onCreateEvent();
                    setIsCreateDropdownOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <CalendarDaysIcon className="h-4 w-4" />
                  Create Event
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Matter Events Summary */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Engagement Events</h2>
          <span className="text-xs text-gray-500">
            {matterEvents.length} events
          </span>
        </div>
      </div>

      {/* Collapsible Filters Section */}
      <div className="flex-1 overflow-y-auto">
        <div className="border-b border-gray-200 dark:border-gray-700">
          {/* Filter Header - Always Visible */}
          <div className="p-3 pb-0">
            <button
              onClick={toggleFiltersExpanded}
              className="w-full flex items-center justify-between text-left hover:bg-gray-50 dark:hover:bg-gray-700 p-2 -m-2 rounded transition-colors"
            >
              <div className="flex items-center gap-2">
                <FunnelIcon className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filters</h3>
                {(matterEventTypeFilters.length > 0 || matterPriorityFilters.length > 0) && (
                  <span className="px-1.5 py-0.5 bg-lex-accent-soft text-lex-accent text-xs rounded-full">
                    {matterEventTypeFilters.length + matterPriorityFilters.length}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {(matterEventTypeFilters.length > 0 || matterPriorityFilters.length > 0) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onClearFilters();
                    }}
                    className="text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 px-2 py-0.5 rounded transition-colors"
                  >
                    Clear
                  </button>
                )}
                {isFiltersExpanded ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                )}
              </div>
            </button>
          </div>

          {/* Collapsible Filter Content */}
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isFiltersExpanded ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0'
          }`}>
            <div className="p-3 pt-2 space-y-3">

              {/* Event Types */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Event Types
                </h4>
                <div className="space-y-1">
                  {EVENT_TYPES.map((type) => {
                    const isActive = matterEventTypeFilters.includes(type.id);
                    const Icon = type.icon;
                    
                    return (
                      <button
                        key={type.id}
                        onClick={() => onToggleEventType(type.id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors ${
                          isActive
                            ? 'bg-lex-accent-soft text-lex-accent border border-lex-accent'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="h-3 w-3 flex-shrink-0" />
                        <span className="truncate">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Priority Levels */}
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Priority
                </h4>
                <div className="space-y-1">
                  {PRIORITY_LEVELS.map((priority) => {
                    const isActive = matterPriorityFilters.includes(priority.id);
                    
                    return (
                      <button
                        key={priority.id}
                        onClick={() => onTogglePriority(priority.id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded transition-colors ${
                          isActive
                            ? 'bg-lex-accent-soft text-lex-accent border border-lex-accent'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                        }`}
                      >
                        <div 
                          className={`w-2 h-2 rounded-full flex-shrink-0`}
                          style={{ backgroundColor: getPriorityColor(priority.color) }}
                        />
                        <span className="truncate capitalize">{priority.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Active Filter Summary */}
              {(matterEventTypeFilters.length > 0 || matterPriorityFilters.length > 0) && (
                <div className="pt-2 border-t border-gray-100">
                  <div className="text-xs text-gray-600 space-y-1">
                    {matterEventTypeFilters.length > 0 && (
                      <div>
                        <span className="font-medium">Types:</span> {matterEventTypeFilters.length} selected
                      </div>
                    )}
                    {matterPriorityFilters.length > 0 && (
                      <div>
                        <span className="font-medium">Priority:</span> {matterPriorityFilters.length} selected
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700">
        <div className="text-xs text-gray-500 text-center">
          {matterEvents.length} event{matterEvents.length !== 1 ? 's' : ''} for this engagement
        </div>
      </div>
    </div>
  );
};

export default MatterCalendarSidebar;
